import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { TrendingUp, FileText, Loader2, ArrowLeft, Copy, Check, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditBoostDispute = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [letter, setLetter] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    dispute_type: '',
    creditor_name: '',
    account_number: '',
    dispute_reason: '',
    additional_details: '',
  });

  const disputeTypes = [
    { value: 'late_payment', label: 'Late Payment (Goodwill Removal)' },
    { value: 'collection', label: 'Collection Account' },
    { value: 'inquiry', label: 'Hard Inquiry' },
    { value: 'error', label: 'Incorrect Information' },
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleGenerate = async () => {
    if (!formData.dispute_type || !formData.creditor_name || !formData.dispute_reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/creditboost/dispute-letter`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLetter(response.data);
      toast.success('Dispute letter generated!');
    } catch (error) {
      toast.error('Failed to generate letter');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(letter.content);
      setCopied(true);
      toast.success('Letter copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([letter.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `Dispute_Letter_${letter.creditor}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Letter downloaded!');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/creditboost/dashboard')} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  CreditBoost
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                <FileText className="w-5 h-5 inline mr-2" />
                Dispute Letter Generator
              </CardTitle>
              <CardDescription>
                Generate professional dispute letters to remove negative items from your credit report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Type of Dispute *</Label>
                <Select
                  value={formData.dispute_type}
                  onValueChange={(value) => handleChange('dispute_type', value)}
                >
                  <SelectTrigger data-testid="select-dispute-type">
                    <SelectValue placeholder="Select dispute type" />
                  </SelectTrigger>
                  <SelectContent>
                    {disputeTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Creditor/Company Name *</Label>
                <Input
                  value={formData.creditor_name}
                  onChange={(e) => handleChange('creditor_name', e.target.value)}
                  placeholder="e.g., Chase Bank, Collection Agency"
                  data-testid="input-creditor"
                />
              </div>

              <div className="space-y-2">
                <Label>Account Number (optional)</Label>
                <Input
                  value={formData.account_number}
                  onChange={(e) => handleChange('account_number', e.target.value)}
                  placeholder="Last 4 digits or full number"
                  data-testid="input-account"
                />
              </div>

              <div className="space-y-2">
                <Label>Reason for Dispute *</Label>
                <Textarea
                  value={formData.dispute_reason}
                  onChange={(e) => handleChange('dispute_reason', e.target.value)}
                  placeholder="e.g., I was never late on this account, This debt is not mine, I already paid this..."
                  rows={3}
                  data-testid="input-reason"
                />
              </div>

              <div className="space-y-2">
                <Label>Additional Details (optional)</Label>
                <Textarea
                  value={formData.additional_details}
                  onChange={(e) => handleChange('additional_details', e.target.value)}
                  placeholder="Any other relevant information..."
                  rows={2}
                  data-testid="input-details"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="generate-letter-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Letter
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Letter Output */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Generated Letter
                </CardTitle>
                <CardDescription>
                  {letter ? 'Your dispute letter is ready' : 'Fill out the form to generate your letter'}
                </CardDescription>
              </div>
              {letter && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {letter ? (
                <div
                  className="p-4 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap leading-relaxed max-h-[500px] overflow-y-auto"
                  data-testid="letter-content"
                >
                  {letter.content}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <FileText className="w-12 h-12 text-slate-300 mb-4" />
                  <p>Your letter will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tips */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tips for Successful Disputes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>• Send letters via certified mail with return receipt requested</li>
              <li>• Keep copies of all correspondence</li>
              <li>• Credit bureaus have 30 days to investigate</li>
              <li>• If disputed items aren't verified, they must be removed</li>
              <li>• You can dispute directly with creditors AND credit bureaus</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreditBoostDispute;
